import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Upload, FolderOpen, Trash2, MoreVertical, FileIcon, Search, Sparkles, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'operations', label: 'Operations' },
  { value: 'safety', label: 'Safety' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'training', label: 'Training' },
  { value: 'policy', label: 'Policy' },
];

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CompanyDocsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);

  const fetchDocs = async () => {
    try {
      const res = await fetch(`${API}/documents`, { credentials: 'include' });
      if (res.ok) setDocuments(await res.json());
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocs(); }, []);

  const onDrop = useCallback((files) => {
    if (files.length > 0) {
      setUploadFile(files[0]);
      setUploadTitle(files[0].name.replace(/\.[^.]+$/, ''));
      setUploadOpen(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
    },
    multiple: false
  });

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle || uploadFile.name);
      formData.append('category', uploadCategory);
      const res = await fetch(`${API}/documents/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (res.ok) {
        toast.success('Document uploaded');
        setUploadOpen(false);
        setUploadFile(null);
        setUploadTitle('');
        fetchDocs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      const res = await fetch(`${API}/documents/${docId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Document deleted');
        fetchDocs();
      }
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleAnalyze = async (doc) => {
    setAnalyzing(doc.doc_id);
    try {
      const res = await fetch(`${API}/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ doc_id: doc.doc_id, doc_type: 'company' })
      });
      if (res.ok) {
        toast.success('Analysis complete — check CASA Library for results');
      } else {
        toast.error('Analysis failed');
      }
    } catch {
      toast.error('Analysis request failed');
    } finally {
      setAnalyzing(null);
    }
  };

  const filtered = documents.filter(d =>
    d.title.toLowerCase().includes(search.toLowerCase()) ||
    d.filename?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="company-docs-page" className="p-6 md:p-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Company Documents
          </h1>
          <p className="text-slate-500 text-sm mt-1">Upload and manage operational documents</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          data-testid="doc-search"
          placeholder="Search documents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600"
        />
      </div>

      {/* Upload Dropzone */}
      <div
        {...getRootProps()}
        data-testid="doc-upload-dropzone"
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-[#0ea5e9] bg-[#0ea5e9]/5'
            : 'border-slate-800 hover:border-slate-700 bg-slate-900/30'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="h-8 w-8 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-400">
          {isDragActive ? 'Drop file here...' : 'Drag & drop a document, or click to select'}
        </p>
        <p className="text-xs text-slate-600 mt-1">PDF, DOC, DOCX, TXT, CSV</p>
      </div>

      {/* Documents Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-[140px] bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FolderOpen className="h-12 w-12 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-500">No documents uploaded yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((doc) => (
            <Card key={doc.doc_id} data-testid={`company-doc-${doc.doc_id}`} className="bg-slate-900 border-slate-800 shadow-sm hover:border-slate-700 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <FileIcon className="h-5 w-5 text-[#0ea5e9]" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-slate-200 truncate">{doc.title}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{doc.filename}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-white">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#0f172a] border-slate-700">
                      <DropdownMenuItem
                        data-testid={`analyze-doc-${doc.doc_id}`}
                        onClick={() => handleAnalyze(doc)}
                        className="text-slate-300 focus:bg-slate-800 focus:text-white cursor-pointer"
                      >
                        <Sparkles className="h-4 w-4 mr-2 text-[#0ea5e9]" />
                        AI Analyze
                      </DropdownMenuItem>
                      {doc.url && (
                        <DropdownMenuItem
                          onClick={() => window.open(doc.url, '_blank')}
                          className="text-slate-300 focus:bg-slate-800 focus:text-white cursor-pointer"
                        >
                          <ExternalLink className="h-4 w-4 mr-2 text-[#0ea5e9]" />
                          View PDF
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        data-testid={`delete-doc-${doc.doc_id}`}
                        onClick={() => handleDelete(doc.doc_id)}
                        className="text-red-400 focus:bg-slate-800 focus:text-red-300 cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">
                    {doc.category}
                  </Badge>
                  <span className="text-[10px] text-slate-600">{formatSize(doc.file_size)}</span>
                  <span className="text-[10px] text-slate-600">
                    {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-[#0f172a] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-200">Upload Document</DialogTitle>
            <DialogDescription className="text-slate-500">
              {uploadFile?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block font-mono uppercase tracking-wider">Title</label>
              <Input
                data-testid="upload-title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                className="bg-slate-900 border-slate-800 text-slate-200"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block font-mono uppercase tracking-wider">Category</label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger data-testid="upload-category" className="bg-slate-900 border-slate-800 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f172a] border-slate-700">
                  {DOC_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value} className="text-slate-300 focus:bg-slate-800 focus:text-white">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setUploadOpen(false)} className="text-slate-400 hover:text-white">Cancel</Button>
            <Button
              data-testid="upload-submit-btn"
              onClick={handleUpload}
              disabled={uploading}
              className="bg-[#003087] hover:bg-[#003087]/90 text-white"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
